import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { useGSAP } from "@/hooks/useGSAP";
import Header from "@/components/landing/layout/Header";
import Footer from "@/components/landing/layout/Footer";

interface CardAccident {
  image: string;
  Heading: string;
  para: string;
}

const cardAccident: CardAccident[] = [
  {
    image: "/ShippingFAQ/loss.png",
    Heading: "Loss",
    para: "A lot can happen between the warehouse and your hands.",
  },
  {
    image: "/ShippingFAQ/damage.png",
    Heading: "Damage",
    para: "Package get shot put over the fence? We got you.",
  },
  {
    image: "/ShippingFAQ/theft.png",
    Heading: "Theft",
    para: "Don't let the porch pirates get you down.",
  },
];

type FaqItem = { q: string; a: string };

const FAQ_ITEMS: FaqItem[] = [
  {
    q: "When does my shipping protection start and end?",
    a: "Shipping protection starts once the package is shipped and ends when the package is in your hands, or, if you file a claim, once we resolve the issue. See the Protega Shipping Protection Guide for full details.",
  },
  {
    q: "What if my purchase ships in multiple packages? Are they all protected?",
    a: "Yep! Your items are covered no matter how many different boxes they ship in.",
  },
  {
    q: "What information do I need to file a claim?",
    a: "When you submit a request, we’ll ask you to tell us if the item(s) were lost, stolen or damaged. If the package contains multiple items we may ask you to confirm which protected items are affected. See the Protega Shipping Protection Guide for full details.",
  },
  {
    q: "When can I file a claim?",
    a: "If your package is damaged, file a claim as soon as it is delivered. If your package is lost or stolen, file a claim 5 days after your estimated delivery date (typically found on the shipping carrier’s tracking site). For packages shipped outside the India, please wait 20 days after your estimated delivery date.",
  },
  {
    q: "Can I cancel my shipping protection?",
    a: "If your order hasn’t shipped yet, please contact the retailer directly to adjust the order. If the order has already shipped, shipping protection is in effect, and a refund cannot be provided. See the Protega Shipping Protection Guide for full details.",
  },
  {
    q: "What’s not protected by Protega Shipping Protection?",
    a: "Not every item is eligible for Protega Shipping Protection. See the Shipping Protection Guide for full details.",
  },
  {
    q: "Can I transfer my shipping protection to other items I bought?",
    a: "No, shipping protection is not transferable to other items not originally protected at the time of purchase. If you want to add shipping protection to a recent purchase that hasn’t shipped yet, contact the retailer directly to see if this is possible.",
  },
];

const AccordionItem: React.FC<{
  item: FaqItem;
  index: number;
  isOpen: boolean;
  onToggle: (i: number) => void;
}> = ({ item, index, isOpen, onToggle }) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [maxH, setMaxH] = useState<string>("0px");

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (isOpen) {
      setMaxH(`${el.scrollHeight}px`);
      const t = setTimeout(() => setMaxH("none"), 300);
      return () => clearTimeout(t);
    } else {
      if (el.scrollHeight && maxH === "none") {
        setMaxH(`${el.scrollHeight}px`);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setMaxH("0px"));
        });
      } else {
        setMaxH("0px");
      }
    }
  }, [isOpen, maxH]);

  return (
    <div className="tw-border-b tw-px-6 tw-text-left tw-border-gray-200">
      <button
        aria-expanded={isOpen}
        aria-controls={`faq-${index}`}
        onClick={() => onToggle(index)}
        className="tw-w-full tw-text-left tw-py-6 tw-flex tw-justify-between tw-items-center tw-gap-4"
      >
        <span className="tw-text-xl tw-font-semibold">{item.q}</span>
        <ChevronDown
          className={`tw-h-6 tw-w-6 tw-transition-transform tw-duration-300 ${isOpen ? "tw-rotate-180" : "tw-rotate-0"}`}
        />
      </button>

      <div
        id={`faq-${index}`}
        ref={contentRef}
        style={{
          maxHeight: maxH === "none" ? undefined : maxH,
          transition: "max-height 300ms ease, opacity 200ms ease",
          opacity: isOpen ? 1 : 0,
          overflow: "hidden",
        }}
        className="tw-pb-6 tw-text-lg tw-text-gray-600"
        aria-hidden={!isOpen}
      >
        <div className="tw-pt-2">{item.a}</div>
      </div>
    </div>
  );
};

const ShippingProtection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleIndex = (i: number) =>
    setOpenIndex((prev) => (prev === i ? null : i));

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const lenis = new Lenis({
      duration: 1.6,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      smoothWheel: true,
      touchMultiplier: 2,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  useGSAP();

  return (
    <div className="tw-min-h-screen tw-bg-white tw-text-black tw-font-mori">
      <Header />
      <main className="tw-relative">
        <div className="tw-h-20 lg:tw-h-24" />

        {/* HERO */}
        <section className="tw-min-h-[85vh] tw-flex tw-items-center tw-justify-center tw-py-20 lg:tw-py-0">
          <div className="tw-w-[90%] tw-mx-auto tw-flex tw-flex-col lg:tw-flex-row tw-items-center tw-gap-16">
            <div className="tw-w-full lg:tw-w-7/12">
              <h1 className="tw-text-5xl md:tw-text-7xl tw-font-bold tw-leading-tight tw-mb-8">
                Protega Shipping Protection
              </h1>
              <p className="tw-text-2xl md:tw-text-3xl tw-text-gray-600 tw-leading-relaxed">
                Make sure it gets there with Protega’s Shipping Protection.
                We’ll replace your package if it’s lost, damaged, or stolen.
              </p>
            </div>
            <div className="tw-w-full lg:tw-w-5/12">
              <img
                className="tw-w-full tw-rounded-[2.5rem] tw-shadow-2xl tw-object-cover"
                src="/ShippingFAQ/hero.png"
                alt="Shipping Protection Hero"
              />
            </div>
          </div>
        </section>

        {/* Relaxation Section */}
        <section className="tw-py-32 tw-bg-[#00C5FB] tw-text-white">
          <div className="tw-w-[90%] tw-mx-auto tw-text-center">
            <h2 className="tw-text-4xl md:tw-text-6xl tw-font-bold tw-mb-20">
              Relax, we’ll replace it.
            </h2>
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-12">
              {cardAccident.map((item, count) => (
                <div
                  key={count}
                  className="tw-bg-white tw-p-10 tw-rounded-[3rem] tw-text-center tw-flex tw-flex-col tw-items-center tw-shadow-xl tw-shadow-black/5"
                >
                  <div className="tw-h-32 tw-flex tw-items-center tw-justify-center tw-mb-8">
                    <img
                      src={item.image}
                      alt={item.Heading}
                      className="tw-max-h-full tw-object-contain"
                    />
                  </div>
                  <h3 className="tw-text-3xl tw-font-bold tw-text-black tw-mb-4">
                    {item.Heading}
                  </h3>
                  <p className="tw-text-lg tw-text-gray-600">{item.para}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Add Protega */}
        <section className="tw-py-32">
          <div className="tw-w-[90%] tw-mx-auto">
            <div className="tw-flex tw-flex-col lg:tw-flex-row tw-items-center tw-gap-20">
              <div className="tw-w-full lg:tw-w-[55%]">
                <h2 className="tw-text-4xl md:tw-text-6xl tw-font-bold tw-mb-8">
                  Why Add Protega Shipping Protection?
                </h2>
                <p className="tw-text-xl md:tw-text-2xl tw-text-gray-600 tw-leading-relaxed">
                  Stop planning your day around a delivery. 24/7 online support,
                  hassle-free replacements, no additional fees.
                </p>
              </div>
              <div className="tw-w-full lg:tw-w-[45%]">
                <img
                  src="/ShippingFAQ/WhyAdd.png"
                  className="tw-w-full tw-rounded-[2.5rem] tw-shadow-2xl"
                  alt="Why Add Protega"
                />
              </div>
            </div>

            {/* Read More Banner */}
            <div className="tw-mt-24 tw-bg-[#00C5FB] tw-rounded-[3rem] tw-p-12 md:tw-p-16 tw-flex tw-flex-col lg:tw-flex-row tw-items-center tw-justify-between tw-gap-8 tw-text-white">
              <div className="tw-text-center lg:tw-text-left">
                <h3 className="tw-text-3xl md:tw-text-4xl tw-font-bold tw-mb-3">
                  Want to read more?
                </h3>
                <p className="tw-text-xl md:tw-text-2xl tw-opacity-90">
                  For more details read the Protega Shipping Protection Guide
                </p>
              </div>
              <Link
                to="read-more"
                className="tw-bg-black tw-text-white hover:tw-bg-gray-900 tw-transition-all tw-px-10 tw-py-4 tw-text-xl tw-font-bold tw-rounded-full"
              >
                Read more
              </Link>
            </div>
          </div>
        </section>

        {/* Delivery Dilemma */}
        <section className="tw-py-32 tw-bg-gray-50">
          <div className="tw-w-[90%] tw-mx-auto">
            <h2 className="tw-text-4xl md:tw-text-6xl tw-font-bold tw-text-center tw-mb-24">
              Delivery dilemma solved
            </h2>
            <div className="tw-grid lg:tw-grid-cols-2 tw-gap-20 tw-items-center">
              <div className="tw-order-2 lg:tw-order-1">
                <img
                  src="/ShippingFAQ/mobile.png"
                  className="tw-w-full tw-max-h-[70vh] tw-object-contain"
                  alt="Delivery Dilemma Mobile"
                />
              </div>
              <div className="tw-order-1 lg:tw-order-2 tw-space-y-16">
                <div className="tw-space-y-4">
                  <span className="tw-text-sm tw-font-bold tw-tracking-widest tw-uppercase tw-text-[#00C5FB]">
                    Step One
                  </span>
                  <h3 className="tw-text-3xl md:tw-text-4xl tw-font-bold">
                    Reach out to Anaya
                  </h3>
                  <p className="tw-text-xl tw-text-gray-600">
                    We provide 24/7 online support with our virtual customer
                    care specialist, Anaya.
                  </p>
                </div>
                <div className="tw-space-y-4">
                  <span className="tw-text-sm tw-font-bold tw-tracking-widest tw-uppercase tw-text-[#00C5FB]">
                    Step Two
                  </span>
                  <h3 className="tw-text-3xl md:tw-text-4xl tw-font-bold">
                    Tracking & Support
                  </h3>
                  <p className="tw-text-xl tw-text-gray-600">
                    Use the tracking link for the latest status and file claims
                    online if needed.
                  </p>
                </div>
                <div className="tw-space-y-4">
                  <span className="tw-text-sm tw-font-bold tw-tracking-widest tw-uppercase tw-text-[#00C5FB]">
                    Step Three
                  </span>
                  <h3 className="tw-text-3xl md:tw-text-4xl tw-font-bold">
                    Hassle-free replacement
                  </h3>
                  <p className="tw-text-xl tw-text-gray-600">
                    If approved, we replace lost or damaged packages quickly.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="tw-py-32">
          <div className="tw-w-[90%] tw-mx-auto">
            <h2 className="tw-text-4xl md:tw-text-6xl tw-font-bold tw-text-center tw-mb-20">
              FAQs
            </h2>
            <div className="tw-max-w-4xl tw-mx-auto tw-bg-white tw-rounded-3xl tw-shadow-xl tw-shadow-black/5 tw-border tw-border-gray-100 tw-overflow-hidden">
              {FAQ_ITEMS.map((item, idx) => (
                <AccordionItem
                  key={idx}
                  item={item}
                  index={idx}
                  isOpen={openIndex === idx}
                  onToggle={toggleIndex}
                />
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default ShippingProtection;
